/**
 * TanStack Router Configuration
 *
 * Routes:
 * - / — Landing page (LandingPage)
 * - /discover — Token discovery and browse
 * - /token/$tokenId — Token detail view
 * - /launch — Token creation wizard
 * - /dashboard — Creator dashboard
 * - /trending — Trending tokens feed
 * - /leaderboards — Platform leaderboards
 * - /admin — Platform administration
 * - * — 404 catch-all
 */

import { Outlet, createRootRoute, createRoute, createRouter } from "@tanstack/react-router";
import { AppLayout } from "./components/layout/AppLayout";
import { AdminPage } from "./pages/AdminPage";
import { CreateTokenPage } from "./pages/CreateTokenPage";
import { CreatorDashboardPage } from "./pages/CreatorDashboardPage";
import { DiscoverPage } from "./pages/DiscoverPage";
import { LandingPage } from "./pages/LandingPage";
import { LeaderboardsPage } from "./pages/LeaderboardsPage";
import { NotFoundPage } from "./pages/NotFoundPage";
import { TokenDetailPage } from "./pages/TokenDetailPage";
import { TrendingPage } from "./pages/TrendingPage";

/**
 * Root route with AppLayout
 */
const rootRoute = createRootRoute({
	component: () => (
		<AppLayout>
			<Outlet />
		</AppLayout>
	),
	notFoundComponent: NotFoundPage,
});

const indexRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/",
	component: LandingPage,
});

const discoverRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/discover",
	component: DiscoverPage,
});

const tokenDetailRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/token/$tokenId",
	component: TokenDetailPage,
});

const launchRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/launch",
	component: CreateTokenPage,
});

const trendingRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/trending",
	component: TrendingPage,
});

const leaderboardsRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/leaderboards",
	component: LeaderboardsPage,
});

const dashboardRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/dashboard",
	component: CreatorDashboardPage,
});

const adminRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/admin",
	component: AdminPage,
});

const notFoundRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "*",
	component: NotFoundPage,
});

/**
 * Route tree
 */
const routeTree = rootRoute.addChildren([
	indexRoute,
	discoverRoute,
	tokenDetailRoute,
	launchRoute,
	trendingRoute,
	leaderboardsRoute,
	dashboardRoute,
	adminRoute,
	notFoundRoute,
]);

/**
 * Router instance
 */
export const router = createRouter({ routeTree });

/**
 * Type declaration for type-safe navigation
 */
declare module "@tanstack/react-router" {
	interface Register {
		router: typeof router;
	}
}
